/**
 * Stereotype-attribuut-derivations — afgeleide attribute-waarden die
 * automatisch worden bijgewerkt wanneer een ander attribuut wijzigt.
 *
 * Gebruikt door de UI-attribute-panel: na de user-edit van het
 * "bron"-attribuut wordt deze helper aangeroepen om de afgeleide
 * waarde mee te patchen in dezelfde concept.attributes-update.
 *
 * Spec-bron: `docs/architecture/presentatie waarden.md` +
 * `docs/architecture/security attributes.md`.
 *
 * Huidige derivations:
 *   1. `risk.Vulnerability.cvss_score` → `cvss_severity`
 *      conform Gerald's 4-band-mapping (presentatie waarden.md):
 *        score === 0       → none   (groen)
 *        0 < score <= 4    → low    (groen)
 *        4 < score <= 7    → medium (oranje)
 *        7 < score <= 10   → high   (rood)
 *
 * Andere derivations die nog geparkeerd zijn (M7+):
 *   - `risk.Risk.{inherent,residual}_{likelihood × impact}` →
 *     `{inherent,residual}_risk_level` via matrix
 *   - `risk.Countermeasure.<base-type>` → `manifestation_level`
 *     via base-type-categorisatie
 *
 * Niet-gemapte waardes (negatief, >10, non-numeric) leveren geen
 * derivation op → returnt leeg object.
 */

import { LEGACY_TYPE_BY_COMPAT_STEREOTYPE } from "./archimate4Migration";

/**
 * Vertaal CVSS-score naar severity-band volgens Gerald's 4-band-spec.
 * Returnt `undefined` voor buiten-bereik-waarden zodat de UI 't oude
 * cvss_severity-attribuut niet overschrijft met een gokwaarde.
 */
export function deriveCvssSeverity(score: number): string | undefined {
  if (!Number.isFinite(score)) return undefined;
  if (score < 0 || score > 10) return undefined;
  if (score === 0) return "none";
  if (score <= 4) return "low";
  if (score <= 7) return "medium";
  return "high";
}

/**
 * Waar een «Countermeasure» zich manifesteert: als intentie, of als concreet
 * business-, applicatie-, technologie- of fysiek element.
 *
 * **Waarom dit uit het profiel komt en niet uit het type.** Vóór ArchiMate 4
 * was het base-type genoeg: een `BusinessFunction` was per definitie business.
 * Na de merge is dat weg — één `archimate.Function` dekt business én
 * applicatie. Het compat-profiel houdt precies het onderscheid vast dat het
 * type kwijtraakte, dus die is nu de bron. Dat is strikt beter dan het oude
 * ontwerp: het overleeft de merge, terwijl het base-type-pad dat niet deed.
 *
 * Nooit-gemergde types (Node, Material, …) worden direct geclassificeerd.
 *
 * Retourneert `undefined` voor een born-v4 concept zonder profiel. Dat is
 * eerlijk: uit een kaal `archimate.Function` valt niet af te leiden of het
 * business of applicatie betreft, en een gok zou stil een verkeerd niveau
 * vastleggen. De UI laat het veld dan met de hand invullen.
 */
const MANIFESTATION_BY_TYPE: Readonly<Record<string, string>> = {
  // Intentie — de maatregel is nog een eis, geen werkend ding.
  "archimate.Requirement": "intent",
  "archimate.Constraint": "intent",
  // Business
  "archimate.BusinessProcess": "business",
  "archimate.BusinessFunction": "business",
  "archimate.BusinessRole": "business",
  "archimate.BusinessService": "business",
  "archimate.BusinessObject": "business",
  // Applicatie
  "archimate.ApplicationFunction": "application",
  "archimate.ApplicationService": "application",
  // Technologie
  "archimate.TechnologyFunction": "technology",
  "archimate.TechnologyService": "technology",
  "archimate.Node": "technology",
  "archimate.SystemSoftware": "technology",
  // Fysiek
  "archimate.Material": "physical",
  "archimate.Equipment": "physical",
  "archimate.Facility": "physical",
  "archimate.Contract": "physical",
};

export function deriveManifestationLevel(
  typeId: string,
  stereotypes: readonly string[] = [],
): string | undefined {
  // Het profiel wint: het draagt het onderscheid dat het generieke type mist.
  // De lookup komt rechtstreeks uit de migratie-module — geen injectie-param,
  // want een caller die 'm vergeet zou stil geen derivatie krijgen.
  for (const s of stereotypes) {
    const legacy = LEGACY_TYPE_BY_COMPAT_STEREOTYPE[s];
    const level = legacy ? MANIFESTATION_BY_TYPE[legacy] : undefined;
    if (level) return level;
  }
  // Nooit-gemergd type — het type zelf is nog specifiek genoeg.
  return MANIFESTATION_BY_TYPE[typeId];
}

/**
 * Generieke entry-point voor de UI-attribute-panel: na een wijziging
 * aan attribuut `changedAttrName` op een concept met stereotype
 * `stereotypeId`, returnt deze helper een (mogelijk lege) record met
 * extra attribute-updates die meegaan in dezelfde patch.
 *
 * Spread-pattern: `{ ...currentAttrs, [changedAttrName]: newValue,
 * ...deriveDependentAttributes(...) }`.
 */
export function deriveDependentAttributes(
  stereotypeId: string,
  changedAttrName: string,
  newValue: unknown,
): Record<string, unknown> {
  if (stereotypeId === "risk.Vulnerability" && changedAttrName === "cvss_score") {
    const raw = typeof newValue === "string" ? Number.parseFloat(newValue) : newValue;
    if (typeof raw !== "number") return {};
    const severity = deriveCvssSeverity(raw);
    return severity ? { cvss_severity: severity } : {};
  }
  return {};
}
